import { TEmailAPI, TEmailAPIFilters, TEmailAPIRightHandMenu } from "./types"
import {
    Button,
    ContentSectionContainer,
    Dropdown,
    EEmailProviders,
    EmailAPIFactory,
    EOperatorTypes,
    fetchRoute,
    FilterBar,
    FullBar,
    GenericWrapperContext,
    Header,
    Icon,
    SideMenu,
    SideMenuContainer,
    TFilterBarupdateReturn,
    TGenericListReturn,
    EEMailSummaryStatuses,
} from "@cakemail-org/ui-components-v2"
import Stack from "@mui/material/Stack"
import "./styles.scss"
import { useContext, useEffect, useRef, useState, MouseEvent } from "react"
import { useTranslation } from "react-i18next"
import { ERoutes } from "../../utils/url"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import { SelectChangeEvent } from "@mui/material/Select/SelectInput"
import { EmailAPIContext } from "./context"
import { getCurrentStoredFilters, setCurrentStoredFilters, handleDocumentationClick } from "../../utils/components"
import { hasPageElementAccess, EPageCustomization, EFeatureFlags, hasFeatureFlag } from "../../utils/limits_features"
import { TAppMenuItems } from "../../components/AppMenu/types"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { EmailAPIReports } from "./containers/reports"
import { LogInspect } from "./components/analytics/logInspect"
import { EmailAPIMetrics } from "./containers/metrics"
import { EmailAPIRequestLogs } from "./containers/requestLogs"
import { EmailAPIDeliverability } from "./containers/deliverability"
import { EmailAPILandingPage } from "../../components/landingPages/emailAPI"
import { EmailReportContainer } from "../emailReport"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { EmailAPIBillingBlock } from "../../components/billing/emailAPI"
import { SideDrawerContainer } from "../../components/sideDrawerContainer"
import { DateRangeDD } from "../../components/dateRangeDropdown"
import { openSupportDialog } from "../../components/dialogs/supportDialog"
import { getLanguageUrlSegment } from "../../data/language"

export function EmailAPI({ }: TEmailAPI) {
    const genericWrapper = useContext(GenericWrapperContext)
    const { partnerBrand, user } = genericWrapper
    const location = useLocation()
    const { t } = useTranslation()

    const emailAPIGuidesEnabled = hasPageElementAccess({
        pageElement: EPageCustomization.emailAPIGuideEnabled,
        partnerBrand
    })

    const documentationItems: TAppMenuItems[] = [
        {
            name: "doc-started",
            text: t("email-api-header-doc-getting-started-heading"),
            icon: "Publish18" as const,
            defaultItemIcon: "Link10" as const
        },
        {
            name: "doc-activity",
            text: t("email-api-header-doc-references-heading"),
            icon: "Book18" as const,
            defaultItemIcon: "Link10" as const
        }
    ]

    const filterStorageKey = "emailApiFilters"
    const initFilters: TEmailAPIFilters = getCurrentStoredFilters<TEmailAPIFilters>({ key: filterStorageKey, accountId: String(genericWrapper.account?.id) })

    //Filtering data
    const [tagsFilter, setTagsFilter] = useState<string[]>(initFilters?.tagsFilter || [])
    const [search, setSearch] = useState<string>(initFilters?.search || "")
    const [status, setStatus] = useState<EEMailSummaryStatuses | "">("")
    const [providerFilter, setProviderFilter] = useState<EEmailProviders[]>(initFilters?.providers || [])
    const [openedSearch, setOpenedSearch] = useState(!!initFilters?.search)
    const [timeRange, setTimeRange] = useState<string>(initFilters?.timeRange || "1d")
    const [filterOperator, setFilterOperator] = useState<EOperatorTypes>(initFilters?.filterOperator || EOperatorTypes.or)
    //todo re add when trend dropdown is back and working with graph
    const [trendRange, setTrendRange] = useState<number>(1 /*initFilters?.trendRange || 3*/)

    //List of email tags
    const [emailTags, setEmailTags] = useState<TGenericListReturn<string>>()

    //Right hand side menu. takes a string then outputs component
    const [rightHandMenu, setRightHandMenu] = useState<TEmailAPIRightHandMenu>()
    // Takes a tagFilter to build an email report
    const [emailReportTagFilters, setEmailReportTagFilters] = useState<any>()

    //Content refresh data
    const [contentKeyId, setContentKeyId] = useState(0)
    const refreshTimeout = useRef<NodeJS.Timeout>(undefined)

    const apiPathName = location.pathname.split("/")[2]
    const apiPathSub = location.pathname.split("/")?.[3]

    useEffect(fetchEmailTags, [])
    useEffect(() => { return () => { clearTimeout(refreshTimeout.current) } }, [])

    function setFilterStorage({ ...filters }: Partial<TEmailAPIFilters> = {}) {
        setCurrentStoredFilters<TEmailAPIFilters>({
            key: filterStorageKey,
            accountId: Number(genericWrapper.account?.id),
            data: {
                timeRange: filters.timeRange || timeRange,
                tagsFilter: filters.tagsFilter || tagsFilter,
                search: filters.search || search,
                trendRange: filters.trendRange || trendRange,
                providers: filters.providers || providerFilter,
                filterOperator: filters.filterOperator || filterOperator,
            }
        })
    }

    function fetchEmailTags(searchTerm?: string) {
        EmailAPIFactory.listTags({ perPage: 100, name: searchTerm ? `~~${searchTerm}` : undefined }).then((data) => {
            setEmailTags(data)
        })
    }

    function handleFilterBarSearchUpdate({ value }: { value: string }) {
        fetchEmailTags(value)
    }

    function handleFilterBarChange({ filterTerms, operator }: TFilterBarupdateReturn) {
        const filters: any = {
            tags: [],
            providers: [],
            partial: []
        }

        filterTerms.forEach((term) => {
            filters[term.tab || "partial"].push(term.value)
        })

        setTagsFilter(filters.tags)
        setProviderFilter(filters.providers)
        setFilterOperator(operator)
        setSearch(filters.partial[0])

        setFilterStorage({
            timeRange: timeRange,
            tagsFilter: filters.tags,
            providers: filters.providers,
            filterOperator: operator,
            search: filters.partial[0]
        })
    }

    function handleRangeChange(event: SelectChangeEvent<string>) {
        const { value } = event.target
        setTimeRange(value)
        setFilterStorage({ timeRange: value })
    }

    function handleStatusChange(event: SelectChangeEvent<string>) {
        const { value } = event.target
        setStatus(value as EEMailSummaryStatuses)
    }

    function handleTrendChange(event: SelectChangeEvent<string>) {
        const { value } = event.target
        setTrendRange(Number(value))
        setFilterStorage({ trendRange: Number(value) })
    }

    function handleSetRightHandMenu(menuInfo?: TEmailAPIRightHandMenu) {
        setRightHandMenu(menuInfo)
    }

    function handleSetEmailReportTagFilters(filter?: any) {
        setEmailReportTagFilters(filter)
    }

    function handleSideNavClick(e: MouseEvent) {
        // Handle documentation links - open in new tab
        // NOTE: documentationItems do not have path properties to prevent default navigation behavior
        handleDocumentationClick(e, user, partnerBrand)

        setSearch("")
        setOpenedSearch(false)
    }

    function handleOpenSupportDialog(exContext: string | undefined = undefined) {
        openSupportDialog({
            dialogTitle: t("landing-page-email-api-request-title"),
            dialogContent: t("landing-page-email-api-request-desc"),
            request: {
                subject: "Get Email API Access",
                message: "I want to check out email API"
            },
            eventTracking: {
                open: {
                    context: "EmailAPI Request Dialog Opened",
                    extendedContext: {
                        name: exContext
                    }
                },
                click: {
                    context: "EmailAPI Request Dialog Sent"
                },
                cancel: {
                    context: "EmailAPI Request Dialog Closed"
                }
            }
        })
    }

    function refreshContent() {
        if (!refreshTimeout.current) {
            setContentKeyId(contentKeyId + 1)
            clearTimeout(refreshTimeout.current)
            refreshTimeout.current = setTimeout(() => {
                refreshTimeout.current = undefined
            }, 5000)
        }
    }

    function handleCreateTagFilter() {
        setRightHandMenu({
            type: "tagFilter",
            id: ""
        })
    }

    const headingStringPart = apiPathSub ? `${apiPathName}-${apiPathSub}` : apiPathName

    const timeRangeEl = <DateRangeDD
        key="email-api-heading-timeRange"
        onChange={handleRangeChange}
        value={String(timeRange)}
    />
    const statusEl = <Dropdown
        color="body1"
        value={status}
        defaultValue="7days"
        onChange={handleStatusChange}
        options={
            [
                {
                    text: t("general-all-status"),
                    value: ""
                },
                {
                    text: t("general-delivered"),
                    value: "delivered"
                },
                {
                    text: t("general-submitted"),
                    value: "submitted"
                },
                {
                    text: t("general-queued"),
                    value: "queued"
                },
                {
                    text: t("general-rejected"),
                    value: "rejected"
                },
                {
                    text: t("general-error"),
                    value: "error"
                },
                {
                    text: t("general-spam"),
                    value: "spam"
                },
                /* no logs of these type...
                    {
                        text: t("general-unsubscribed"),
                        value: "unsubscribed"
                    },
                    {
                        text: t("general-global_unsubscribed"),
                        value: "global_unsuscribed"
                    },
                    Doesnt work yet in BE... 
                    {
                            text: t("other"),
                            value: "other"
                    },
                    {
                            text: t("general-bounced"),
                            value: "bounce"
                    },
                */
            ]}
    />

    const providers: TDropMenuItem[] = Object.values(EEmailProviders).map((p) => {
        return {
            text: p,
            value: p
        }
    })

    const otherProviderIndex = providers.findIndex(p => p.text === EEmailProviders.other)

    //For translation purpose
    if (otherProviderIndex !== -1) {
        providers[otherProviderIndex].text = t("email-log-type-other")
    }

    /* const trendRangeEl = <Dropdown
         key="email-api-heading-trend"
         color="body1"
         value={String(trendRange)}
         defaultValue="3"
         onChange={handleTrendChange}
         options={[
             {
                 text: t("general-x-trend", { count: 3 }),
                 value: "3"
             },
             {
                 text: t("general-x-trend", { count: 5 }),
                 value: "5"
             }
         ]}
     />*/

    const refreshEl = <Button key="email-api-heading-refresh" iconOnly variant="outlined" color="body1" onClick={refreshContent}><Icon name="Refresh18" /></Button>

    const createEditReport = <Button key="email-api-heading-create-report" iconOnly variant="outlined" color="body1" onClick={handleCreateTagFilter}><Icon name="Add18" /></Button>

    let rightMenu: any[] = []
    switch (apiPathName) {
        case "metrics":
            rightMenu = [
                refreshEl,
                timeRangeEl
            ]
            break
        case "deliverability":
            rightMenu = [
                refreshEl,
                timeRangeEl
            ]
            break
        case "reports":
            rightMenu = [
                refreshEl,
                timeRangeEl,
                createEditReport
            ]
            break
        case "requestLog":
            rightMenu = [
                refreshEl,
                timeRangeEl,
                statusEl
            ]
            break
        case "doc":
            break
    }

    const filterBarTerms = [
        ...tagsFilter?.map((t) => {
            return { tab: "tags", text: t, value: t }
        }) || [],
        ...providerFilter?.map((p) => {
            return { tab: "providers", text: p, value: p }
        }) || []
    ]

    if (search) {
        filterBarTerms.push({ tab: "partial", text: search, value: search })
    }

    const filterBar = <FilterBar
        onUpdate={handleFilterBarChange}
        onSearchUpdate={handleFilterBarSearchUpdate}
        operatorOptions={[
            {
                text: t("filter-bar-condition-match-any"),
                value: "or"
            },
            {
                text: t("filter-bar-condition-match-all"),
                value: "and"
            }
        ]}
        addFilterTxt={t("filter-bar-add-filter")}
        saveTxt={t("general-save")}
        clearTxt={t("general-clear")}
        searchPlaceHolder={t("email-api-filter-bar-search-placeholder")}
        dropMenuOptions={[
            ...emailTags?.data?.map((t) => {
                return { tab: "tags", text: t, value: t }
            }) || [],
            ...Object.values(EEmailProviders).map((p) => {
                return { tab: "providers", text: p, value: p }
            })
        ]}
        dropMenuProps={{
            tabs: [
                { name: "tags", text: t("general-tag_other") },
                { name: "providers", text: t("general-provider_other") }
            ],
            disabledTabs: apiPathName !== "requestLog" ? ["partial"] : [],
            options: {
                tags: {
                    emptyText: {
                        desc: t("filter-bar-no-match-email"),
                        text: t("filter-bar-no-match-title")
                    },
                    disableLocalSearch: true
                },
                providers: {
                    emptyText: {
                        desc: t("filter-bar-no-match-email"),
                        text: t("filter-bar-no-match-title")
                    }
                }
            }
        }}

        filterTerms={filterBarTerms}
        replacePartial
        //filterTerms={[]}
        //onSave={}
        //initOperatorValue=""
        operatorValue={filterOperator}

    />

    const hasAccessToFeature = genericWrapper.account?.usage_limits?.use_email_api

    const showNewMenu = hasFeatureFlag({ flag: EFeatureFlags.newMenu, partnerBrand: genericWrapper.partnerBrand })

    return <Stack direction="row" className="emailAPIContainer">
        {!hasAccessToFeature &&
            <EmailAPILandingPage mainCTA={handleOpenSupportDialog} />
        }
        {hasAccessToFeature &&
            <>
                {(!rightHandMenu && !showNewMenu) &&
                    <SideMenu>
                        <SideMenuContainer
                            menuItemClick={(e) => handleSideNavClick(e)}
                            menuItems={[
                                {
                                    text: t("email-api-header-api-heading"),
                                    variant: "section",
                                    name: "api-heading"
                                },
                                {
                                    name: "metrics",
                                    path: "#" + fetchRoute({ route: ERoutes.emailMetrics }),
                                    text: t("email-api-header-metrics-heading"),
                                    icon: "LineChart18",
                                    defaultItemIcon: false
                                },
                                {
                                    name: "deliverability",
                                    path: "#" + fetchRoute({ route: ERoutes.emailDeliverability }),
                                    text: t("email-api-header-deliverability-heading"),
                                    icon: "Bounce18",
                                    defaultItemIcon: false
                                },
                                /*{
                                    name: "reports",
                                    path: "#" + fetchRoute({ route: ERoutes.emailReports }),
                                    text: t("email-api-header-reports-heading"),
                                    icon: "Sends24",
                                },*/
                                {
                                    name: "requestLogs",
                                    path: "#" + fetchRoute({ route: ERoutes.emailRequestLog }),
                                    text: t("email-api-header-requestLog-heading"),
                                    icon: "Activity18",
                                    defaultItemIcon: false
                                },
                                ...(emailAPIGuidesEnabled ? documentationItems : []),
                            ]}
                        />
                        <EmailAPIBillingBlock />
                    </SideMenu>
                }

                <EmailAPIContext.Provider value={{
                    timeRange: timeRange,
                    trendRange: Number(trendRange),
                    emailTags,
                    tagsFilter,
                    search,
                    status,
                    rightHandMenu,
                    setRightHandMenu: handleSetRightHandMenu,
                    providerFilter,
                    filterOperator,
                    setEmailReportTagFilters: handleSetEmailReportTagFilters,
                    refreshKey: contentKeyId
                }}>
                    <ContentSectionContainer>
                        <FullBar
                            left={<Header
                                headingVariant="titleL"
                                heading={t(`email-api-header-${headingStringPart}-heading`)}
                                headingTooltip={t(`email-api-header-${headingStringPart}-heading-tooltip`)}
                                support={t(`email-api-header-api-heading`)}
                            />}
                            right={rightMenu}
                        />

                        {filterBar}

                        <Stack className="subContentSectionContainer">
                            <Routes>
                                <Route path={fetchRoute({ route: ERoutes.emailMetrics })} element={<EmailAPIMetrics />} />
                                <Route path={fetchRoute({ route: ERoutes.emailDeliverability })} element={<EmailAPIDeliverability />} />
                                <Route path={fetchRoute({ route: ERoutes.emailReports })} element={<EmailAPIReports />} />
                                <Route path={fetchRoute({ route: ERoutes.emailRequestLog })} element={<EmailAPIRequestLogs />} />

                                <Route path={fetchRoute({ route: ERoutes.apiGettingStarted })} element={<h1>Getting started</h1>} />
                                <Route path={fetchRoute({ route: ERoutes.apiReferences })} element={<h1>References</h1>} />

                                <Route index path={"metrics"} element={<EmailAPIMetrics />} />
                                <Route path={"deliverability"} element={<EmailAPIDeliverability />} />
                                <Route path={"reports"} element={<EmailAPIReports />} />
                                <Route path={"requestLog"} element={<EmailAPIRequestLogs />} />


                                <Route path={"doc/getting-started"} element={<h1>Getting Started</h1>} />
                                <Route path={"doc/guide-reference"} element={<h1>Guide & Reference</h1>} />

                                <Route
                                    path="*"
                                    element={<Navigate to={fetchRoute({ route: ERoutes.emailMetrics })} replace />}
                                />

                            </Routes>
                        </Stack>
                    </ContentSectionContainer>


                    {rightHandMenu && <SideDrawerContainer>
                        {rightHandMenu.type === "logInspect" &&
                            <LogInspect emailId={rightHandMenu.id} refreshKey={contentKeyId} />
                        }

                    </SideDrawerContainer>}
                    {emailReportTagFilters && <EmailReportContainer onClose={() => { setEmailReportTagFilters(undefined) }} tagFilter={emailReportTagFilters} />}
                </EmailAPIContext.Provider>
            </>
        }
    </Stack>
}